// path: frontend/src/hooks/useQuizGenerator.ts
import { useState, useCallback } from "react";
import { generateQuiz, type QuizQuestion } from "../services/quizApi";

/** Helper types for stricter internal handling */
type RawQuiz =
  | QuizQuestion[]
  | { questions?: QuizQuestion[] }
  | null
  | undefined;

const LETTERS = ["A", "B", "C", "D"] as const;

const norm = (s: unknown) =>
  String(s ?? "").replace(/[^a-z0-9]/gi, "").toLowerCase();

/** Remove brackets/meta & boilerplate like "Sample question 1?" or "Q1:" */
function cleanQuestionStem(s: unknown): string {
  let t = String(s ?? "").trim();
  t = t.replace(/\[[^\]]*\]/g, " ").trim();
  t = t.replace(/^(sample\s+question|question|q)\s*\d*\s*[:\-–.]?\s*/i, "").trim();
  t = t.replace(/^\d+\s*[.)-]\s*/, "").trim();
  t = t.replace(/\s{2,}/g, " ");
  return t;
}

/** Remove empties & de-dupe by normalized text, keep original order. */
function dedupeOptions(options: unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of options ?? []) {
    let s = String(raw ?? "").trim();
    if (!s) continue;
    s = s.replace(/^\(?[A-Da-d][\)\]:．。-]\s*/, "").trim();
    const key = norm(s);
    if (!key) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/** Try to split a single string containing multiple options into an array */
function maybeSplitPackedOptions(opt: unknown): string[] | null {
  const s = String(opt ?? "");
  const chunks = s.split(/\s(?=[(]?[A-Da-d][\)\]:．。-]\s)/g);
  if (chunks.length >= 4) return chunks;
  return null;
}

/** Normalize to exactly four options (A–D). */
function normalizeToFour(rawOptions: unknown): string[] | null {
  let list: string[] = [];

  if (Array.isArray(rawOptions)) {
    list = dedupeOptions(rawOptions);
  } else if (typeof rawOptions === "string") {
    const split = maybeSplitPackedOptions(rawOptions);
    list = split ? dedupeOptions(split) : dedupeOptions([rawOptions]);
  } else {
    list = [];
  }

  if (list.length < 4) return null;
  return list.slice(0, 4);
}

/** Resolve letter (A–D) or text to the canonical option text */
function resolveAnswerToText(answerRaw: unknown, options: string[]): string | null {
  const answer = String(answerRaw ?? "").trim();

  const m = /^[\(\s]*([A-Da-d])[\)\s:\-–．。-]?$/.exec(answer);
  if (m && typeof m[1] === "string") {
    const letter = m[1].toUpperCase() as (typeof LETTERS)[number];
    const idx = LETTERS.indexOf(letter);
    if (idx !== -1 && idx < options.length) {
      const val = options[idx];
      if (typeof val === "string") return val;
      return null;
    }
  }

  const ansNorm = norm(answer);
  if (ansNorm) {
    for (const opt of options) {
      if (norm(opt) === ansNorm) return opt;
    }
  }

  return null;
}

/** Validate & clean a single QuizQuestion from the backend. */
function validateQuestion(raw: QuizQuestion): QuizQuestion | null {
  const question = cleanQuestionStem(raw?.question);
  if (!question) return null;

  const options = normalizeToFour((raw as any)?.options);
  if (!options) return null;

  const answerText = resolveAnswerToText((raw as any)?.answer, options);
  if (!answerText) return null;

  const explanation = String((raw as any)?.explanation ?? "").trim();

  // Preserve rationales if they exist
  const rationales = (raw as any)?.rationales;

  return {
    question,
    options,
    answer: answerText,
    explanation: explanation || undefined,
    ...(rationales && Object.keys(rationales).length > 0 ? { rationales } : {}),
  };
}

/** Normalize backend payload shape to an array of raw questions. */
function coerceToArray(payload: RawQuiz): QuizQuestion[] {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.questions)) return payload.questions;
  return [];
}

// 🔒 AUTHENTICATION: User email and auth token are handled automatically by quizApi.ts
export function useQuizGenerator() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuiz = useCallback(
    async (
      topic: string,
      numQuestions: number,
      options?: {
        language_code?: string;
        difficulty?: string;
        explanation_mode?: string;
      }
    ) => {
      setLoading(true);
      setError(null);

      try {
        // Validation
        if (!topic || !topic.trim()) {
          throw new Error("Topic is required.");
        }
        const n = Number(numQuestions);
        if (!Number.isFinite(n) || n < 1 || n > 20) {
          throw new Error("Number of questions must be between 1 and 20.");
        }

        console.log("🎯 fetchQuiz called:", { topic, numQuestions: n, options });

        // 🔒 Call authenticated API (quizApi.ts handles Firebase auth token)
        const payload = await generateQuiz({
          topic: topic.trim(),
          num_questions: n,
          language_code: options?.language_code || "en-US",
          difficulty: options?.difficulty || "moderate",
          explanation_mode: options?.explanation_mode || "full",
          need_distractor_reasons: true,
        });

        const raw = coerceToArray(payload);
        console.log("✅ Raw questions received:", raw.length);

        if (raw.length === 0) {
          throw new Error("No questions returned from API.");
        }

        // Validate and clean each question
        const valid: QuizQuestion[] = [];
        const seenQ = new Set<string>();

        for (const item of raw) {
          const cleaned = validateQuestion(item);
          if (!cleaned) {
            console.warn("⚠️ Skipped invalid question:", item);
            continue;
          }
          const key = norm(cleaned.question);
          if (seenQ.has(key)) {
            console.warn("⚠️ Skipped duplicate question:", cleaned.question);
            continue;
          }
          seenQ.add(key);
          valid.push(cleaned);
          if (valid.length === n) break;
        }

        if (valid.length === 0) {
          throw new Error("No valid questions after validation. The API may have returned invalid data.");
        }

        console.log(`✅ fetchQuiz success: ${valid.length} valid questions out of ${raw.length} raw`);
        return valid;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Unknown error during quiz generation";
        console.error("❌ fetchQuiz failed:", message, err);
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [] // 🔒 No dependencies needed - auth is handled in service layer
  );

  return { fetchQuiz, loading, error };
}